import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, Popover, Tooltip, Typography } from "@mui/material";
import { OFFER_ICONS, offerIconKey, offerIconLabel } from "@shared/offerIcons";
import { tokens } from "../../theme";
import OfferIcon from "./OfferIcon";

interface Props {
  /** The stored key, or anything else — unknown values draw the fallback. */
  value: string | undefined;
  onChange: (next: string) => void;
  /** Names the row in the aria-label, e.g. "Handdukar". */
  rowLabel: string;
  testId?: string;
}

/**
 * Glyph tiles drawn by the TV's own component. Both catalogue labels show under
 * each glyph whatever the CMS language: they name what the guest sees.
 */
export default function OfferIconPicker({
  value,
  onChange,
  rowLabel,
  testId,
}: Props) {
  const { t } = useTranslation("offers");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = offerIconKey(value);
  const name = rowLabel.trim() || t("offerIconPicker.thisItem");
  const iconFor = t("offerIconPicker.iconFor", { name });

  return (
    <>
      <Tooltip
        title={t("offerIconPicker.tooltip", { label: offerIconLabel(current) })}
      >
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={iconFor}
          data-testid={testId}
          sx={{
            width: 44,
            height: 44,
            flex: "none",
            borderRadius: "10px",
            border: "1px solid",
            borderColor: tokens.divider,
            bgcolor: tokens.rowAlt,
            color: tokens.ink,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { borderColor: tokens.brand },
          }}
        >
          <OfferIcon name={current} size={24} />
        </ButtonBase>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          data-testid={testId ? `${testId}-grid` : undefined}
          role="radiogroup"
          aria-label={iconFor}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 84px)",
            gap: 0.5,
            p: 1,
          }}
        >
          {OFFER_ICONS.map((def) => {
            const on = def.key === current;
            return (
              <ButtonBase
                key={def.key}
                role="radio"
                aria-checked={on}
                aria-label={`${def.label.en} · ${def.label.sv}`}
                data-testid={testId ? `${testId}-${def.key}` : undefined}
                onClick={() => {
                  onChange(def.key);
                  setAnchor(null);
                }}
                sx={{
                  flexDirection: "column",
                  gap: 0.5,
                  py: 1,
                  px: 0.5,
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: on ? tokens.brand : "transparent",
                  bgcolor: on ? tokens.brandTintSoft : "transparent",
                  color: tokens.ink,
                  "&:hover": { bgcolor: tokens.rowAlt },
                }}
              >
                <OfferIcon name={def.key} size={26} />
                <Typography
                  variant="micro"
                  sx={{
                    lineHeight: 1.2,
                    textAlign: "center",
                    color: tokens.inkMuted,
                  }}
                >
                  {def.label.en}
                </Typography>
                <Typography
                  variant="micro"
                  sx={{
                    lineHeight: 1.2,
                    textAlign: "center",
                    color: tokens.inkFaint,
                  }}
                >
                  {def.label.sv}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
